/**
 * ProposalsTab.js
 * Admin Tab for managing "Wisdom of Stars" submissions.
 */
import { getFirestore, collection, query, orderBy, getDocs, doc, updateDoc, Timestamp } from "firebase/firestore";
import { app } from "../../../firebase-config.js";

const db = getFirestore(app);

export const ProposalsTab = async (container) => {
    container.innerHTML = `
        <div class="bg-white rounded-lg shadow p-6">
            <h3 class="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                <span class="material-symbols-outlined text-yellow-500">lightbulb</span>
                Wisdom of Stars Proposals
            </h3>
            
            <div class="overflow-x-auto">
                <table class="min-w-full divide-y divide-gray-200">
                    <thead class="bg-gray-50">
                        <tr>
                            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title / Content</th>
                            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                            <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                    </thead>
                    <tbody id="proposals-table-body" class="bg-white divide-y divide-gray-200">
                        <!-- Loaded dynamically -->
                    </tbody>
                </table>
                <div id="loading-indicator" class="py-12 text-center text-gray-400">
                    Loading proposals...
                </div>
            </div>
        </div>
    `;

    const tbody = container.querySelector('#proposals-table-body');
    const loading = container.querySelector('#loading-indicator');

    try {
        const q = query(
            collection(db, "proposals"),
            orderBy("createdAt", "desc")
        );

        const snapshot = await getDocs(q);

        loading.style.display = 'none';

        if (snapshot.empty) {
            tbody.innerHTML = `<tr><td colspan="5" class="px-6 py-12 text-center text-gray-400">No proposals found yet.</td></tr>`;
            return;
        }

        // Helper to format date
        const formatDate = (ts) => {
            if (!ts) return '-';
            const date = ts.toDate ? ts.toDate() : new Date(ts);
            return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
        };

        // Render Rows
        snapshot.forEach(docSnap => {
            const data = docSnap.data();
            const id = docSnap.id;

            const tr = document.createElement('tr');
            tr.className = "hover:bg-gray-50 transition-colors";

            tr.innerHTML = `
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    ${formatDate(data.createdAt)}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div class="font-medium">${data.userName || 'Anonymous'}</div>
                    <div class="text-xs text-gray-400">${data.userEmail || '-'}</div>
                </td>
                <td class="px-6 py-4 text-sm text-gray-700 max-w-sm">
                    <div class="font-bold truncate" title="${data.title}">${data.title}</div>
                    <div class="text-gray-500 line-clamp-2 mt-1 break-words leading-relaxed text-xs">
                        ${data.content}
                    </div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                    <span class="inline-flex px-2 text-xs font-semibold leading-5 rounded-full 
                        ${data.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                    data.status === 'approved' ? 'bg-green-100 text-green-800' :
                        data.status === 'rejected' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'}">
                        ${data.status.toUpperCase()}
                    </span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                    <button class="btn-approve text-green-600 hover:text-green-900" title="Approve">
                        <span class="material-symbols-outlined">check_circle</span>
                    </button>
                    <button class="btn-reject text-red-600 hover:text-red-900" title="Reject">
                        <span class="material-symbols-outlined">cancel</span>
                    </button>
                </td>
            `;

            // Bind Actions
            const btnApprove = tr.querySelector('.btn-approve');
            const btnReject = tr.querySelector('.btn-reject');

            const updateStatus = async (newStatus) => {
                const confirmed = await window.Modal.confirm(`Mark this proposal as ${newStatus}?`);
                if (!confirmed) return;

                try {
                    await updateDoc(doc(db, "proposals", id), {
                        status: newStatus,
                        updatedAt: Timestamp.now()
                    });
                    // Simple reload or DOM update
                    data.status = newStatus;
                    const statusSpan = tr.querySelector('span.inline-flex');
                    statusSpan.className = `inline-flex px-2 text-xs font-semibold leading-5 rounded-full ${newStatus === 'approved' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`;
                    statusSpan.textContent = newStatus.toUpperCase();
                } catch (e) {
                    console.error(e);
                    alert("Update failed: " + e.message);
                }
            };

            btnApprove.onclick = () => updateStatus('approved');
            btnReject.onclick = () => updateStatus('rejected');

            tbody.appendChild(tr);
        });

    } catch (error) {
        console.error("Error loading proposals:", error);
        loading.innerHTML = `<span class="text-red-500">Failed to load: ${error.message}</span>`;
    }
};
